import { useParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { StatusPill, Skeleton } from '../components/ui';
import { api, type Invoice } from '../lib/api';
import { useAction, useLoad } from '../lib/hooks';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

interface Payload {
  invoice: Invoice & { address?: string; postcode?: string; email?: string };
  tenant: any;
}

const NEXT_STATUS: Record<string, { to: string; label: string }[]> = {
  draft: [{ to: 'sent', label: 'Mark as sent' }],
  sent: [
    { to: 'paid', label: 'Mark as paid' },
    { to: 'draft', label: 'Back to draft' },
  ],
  overdue: [{ to: 'paid', label: 'Mark as paid' }],
  paid: [{ to: 'sent', label: 'Mark as unpaid' }],
};

export default function InvoiceView() {
  const { id } = useParams();
  const { data, loading, error, reload } = useLoad<Payload>(() => api.get(`/invoices/${id}`), [id]);
  const { busy, error: actionError, run } = useAction();

  if (loading && !data) {
    return (
      <Screen title="Invoice" back="/invoices">
        <Skeleton variant="detail" rows={4} />
      </Screen>
    );
  }
  if (error || !data) {
    return (
      <Screen title="Invoice" back="/invoices">
        <div className="notice notice-danger">{error ?? 'Not found.'}</div>
      </Screen>
    );
  }

  const { invoice, tenant } = data;
  const actions = NEXT_STATUS[invoice.status] ?? [];

  const setStatus = (status: string) => run(async () => {
    await api.patch(`/invoices/${invoice.id}`, { status });
    reload();
  });

  return (
    <Screen
      title={invoice.number}
      subtitle={invoice.customer_name}
      back="/invoices"
      action={
        <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>
          Print
        </button>
      }
    >
      <div className="stack-4">
        {actionError ? <div className="notice notice-danger">{actionError}</div> : null}

        <div className="row spread no-print">
          <StatusPill
            state={invoice.status === 'paid' ? 'ok' : invoice.status === 'overdue' ? 'overdue' : invoice.status === 'draft' ? 'unknown' : 'due'}
          >
            {invoice.status}
          </StatusPill>
          <div className="row row-tight">
            {actions.map((action) => (
              <button
                key={action.to}
                className="btn btn-ghost btn-sm"
                onClick={() => setStatus(action.to)}
                disabled={busy}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        <article className="invoice-doc stack-5">
          <header className="invoice-head">
            <div>
              <h2>{tenant?.org_name ?? tenant?.name}</h2>
              <p className="small muted" style={{ marginTop: 4 }}>
                {tenant?.address}
                {tenant?.postcode ? <br /> : null}
                {tenant?.postcode}
                {tenant?.phone ? <br /> : null}
                {tenant?.phone}
              </p>
              {tenant?.vat_number ? <p className="tiny dim">VAT {tenant.vat_number}</p> : null}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="eyebrow">Invoice</div>
              <div className="mono" style={{ fontSize: '1.125rem', fontWeight: 700 }}>
                {invoice.number}
              </div>
              <p className="small muted" style={{ marginTop: 4 }}>
                Issued {formatDate(invoice.issue_date)}
                {invoice.due_date ? (
                  <>
                    <br />
                    Due {formatDate(invoice.due_date)}
                  </>
                ) : null}
              </p>
            </div>
          </header>

          <div>
            <div className="eyebrow">Bill to</div>
            <p style={{ fontWeight: 600, marginTop: 4 }}>{invoice.customer_name}</p>
            <p className="small muted">
              {invoice.address}
              {invoice.postcode ? `, ${invoice.postcode}` : ''}
            </p>
          </div>

          <div className="table-wrap">
            <table className="data invoice-lines">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="num">Qty</th>
                  <th className="num">Unit</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line, index) => (
                  <tr key={index}>
                    <td>{line.desc}</td>
                    <td className="num">{Number(line.qty)}</td>
                    <td className="num">£{Number(line.unitPrice).toFixed(2)}</td>
                    <td className="num money">£{(Number(line.qty) * Number(line.unitPrice)).toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="num">
                    Subtotal
                  </td>
                  <td className="num money">£{Number(invoice.subtotal).toFixed(2)}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="num">
                    VAT at {Number(invoice.vat_rate)}%
                  </td>
                  <td className="num money">£{Number(invoice.vat).toFixed(2)}</td>
                </tr>
                <tr className="invoice-total-row">
                  <td colSpan={3} className="num">
                    Total
                  </td>
                  <td className="num money">£{Number(invoice.total).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {invoice.notes ? <p className="small muted">{invoice.notes}</p> : null}

          {invoice.paid_at ? (
            <p className="small" style={{ color: 'var(--ok)', fontWeight: 600 }}>
              Paid {formatDate(invoice.paid_at.slice(0, 10))}
            </p>
          ) : null}
        </article>
      </div>
    </Screen>
  );
}
